/** San Francisco congestion pricing: a guided comparison of pre-computed
 *  scenarios, on two sidebar pages:
 *    od       Origin-Destination Analytics, three views:
 *               Trips into the zone  car trips into the charging zone, as arrows from
 *                                    origin to destination, as hexagons by where they
 *                                    start, or both: Baseline, or Scenario showing the
 *                                    trips that remain, the shifted trips or the share
 *                                    shifted
 *               Analytics            a metric on the map, by link, hexagon, city
 *                                    hexagon or tract, with charts (AnalyticsPage);
 *                                    its V/C metric is the pricing map: V/C on the
 *                                    roads in view, opening on the charging zone,
 *                                    Baseline / Scenario / Difference
 *               Accessibility        the Bay Area's hexagons; a click on one
 *                                    colours the others by its car trips to each,
 *                                    or by travel time from it by walk, bike, drive
 *                                    or transit (Valhalla, live)
 *             All three have the headline cards, and draw the protected neighbourhoods
 *             while the route-cost scenario is the one chosen
 *    compare  Scenario Comparison: a map per scenario, then the metric grid
 *  The selected scenario carries over between the two. Nothing here starts a simulation. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { FaSpinner, FaInfoCircle } from 'react-icons/fa';
import WorkshopMap, { ThumbMap, BandsLegendRow } from './WorkshopMap';
import FlowMap from './FlowMap';
import HexMap from './HexMap';
import WorkshopBoundary from './WorkshopBoundary';
import AnalyticsPage, { MetricPicker } from './AnalyticsPage';
import { METRICS, metricOf, getTotals } from './analytics';
import { fetchWorkshop, getRoads, getViewRoads, getFlows, getOdAll, getBandOutlines, roadTier } from './api';
import { metricById } from './format';
import { ScenarioList, ImpactCards, ComparisonGrid, CorridorPanel, ModelTag, ScenarioLevelToggle } from './panels';
import { periodsOf, runNotice, routeCostId, sameTripsAs } from './figures';
import { FLOW_MODES, SCENARIO_SHOWS, flowView, flowTotals, tazOf } from './flows';
import {
  getHexGrid, getHexCarTrips, getHexZoneTrips, hexDistanceKm, tripBins, shareBins, zoneValues,
  SHARE_MIN_TRIPS, getReach, reachKey, reachHexes, reachColor, REACH_MODES, REACH_BANDS, TRANSIT_DEFAULT_HOUR,
} from './hexes';
import { hourSpan } from '../../utils/trafficDataset';
import { DEFAULT_SERVICE_LEVEL } from '../../utils/serviceLevels';
import { SHOW_OD_PRICING_TABS } from '../../config/features';
import './Workshop.css';

const CBSA = '41860';

/** `workshop` with each scenario that has service-level variants shown at `level`: its maps
 *  read the variant's run, the model's figures under its id are the variant's, and its
 *  `level` is the variant. */
function atServiceLevel(workshop, level) {
  const model = workshop.model;
  const figures = model ? { ...model.scenarios } : null;
  const scenarios = workshop.scenarios.map((s) => {
    const v = s.variants && (s.variants.find((x) => x.service_level === level)
      || s.variants.find((x) => x.run_scenario === s.map.run_scenario));
    if (!v) return s;
    if (figures && v.run_scenario !== s.map.run_scenario) figures[s.id] = figures[v.run_scenario];
    return { ...s, level: v, map: { ...s.map, run_scenario: v.run_scenario, note: v.note || s.map.note } };
  });
  return { ...workshop, scenarios, model: model && { ...model, scenarios: figures } };
}

/** The sidebar entry that opens each page. */
export const WORKSHOP_PAGES = [
  { id: 'od', menu: 'Origin-Destination Analytics' },
  { id: 'compare', menu: 'Scenario Comparison' },
];
const menuFor = (id) => WORKSHOP_PAGES.find((p) => p.id === id).menu;

/** The views behind the Origin-Destination Analytics entry. */
export const OD_VIEWS = [
  { id: 'flows', label: 'Trips into the zone' },
  { id: 'pricing', label: 'Analytics' },
  { id: 'hexes', label: 'Accessibility' },
].filter((v) => SHOW_OD_PRICING_TABS || v.id !== 'flows');

export function OdViewSwitch({ value, onChange, views = OD_VIEWS }) {
  if (views.length < 2) return null;
  return (
    <div className="ws-viewswitch" role="tablist">
      {views.map((v) => (
        <button
          key={v.id}
          role="tab"
          aria-selected={value === v.id}
          className={value === v.id ? 'on' : ''}
          onClick={() => onChange(v.id)}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

const ROAD_MODES = [
  { id: 'baseline', label: 'Baseline' },
  { id: 'scenario', label: 'Scenario' },
  { id: 'difference', label: 'Difference' },
];
// The flow map's: what Scenario shows is a list under it (SCENARIO_SHOWS).
const FLOW_TOP = ROAD_MODES.filter((m) => m.id !== 'difference');

export default function Workshop(props) {
  return (
    <WorkshopBoundary resetKey={`${props.page}:${props.odView}`}>
      <WorkshopPages {...props} />
    </WorkshopBoundary>
  );
}

function WorkshopPages({ page = 'od', onNavigate, odView = 'flows', onOdView,
  serviceLevel = DEFAULT_SERVICE_LEVEL, onServiceLevel }) {
  const [loaded, setWorkshop] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState('uniform');
  const [periodId, setPeriodId] = useState(null);
  // Analytics: the metric, level and hour, kept while other views are open. The first
  // metric offered is V/C while the pricing map is shown (SHOW_OD_PRICING_TABS).
  const [anMetric, setAnMetric] = useState(METRICS[0].id);
  const [anScenario, setAnScenario] = useState('baseline');
  const [anLevel, setAnLevel] = useState('link');
  const [anHour, setAnHour] = useState(8);

  useEffect(() => {
    let cancelled = false;
    fetchWorkshop(CBSA)
      .then((w) => { if (!cancelled) setWorkshop(w); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  const metrics = useMemo(() => (loaded ? metricById(loaded) : {}), [loaded]);
  // Scenario 5 at the service level the pricing panel shares.
  // The headline cards' real figures, from the Analytics build of the aq-health export.
  const [analyticsTotals, setAnalyticsTotals] = useState(null);
  useEffect(() => { getTotals(CBSA).then(setAnalyticsTotals).catch(() => setAnalyticsTotals(null)); }, []);
  const workshop = useMemo(
    () => (loaded ? { ...atServiceLevel(loaded, serviceLevel), analyticsTotals } : null),
    [loaded, serviceLevel, analyticsTotals],
  );

  if (error) return <div className="ws-root"><div className="ws-error">{error}</div></div>;
  if (!workshop) {
    return <div className="ws-root"><div className="ws-loading"><FaSpinner className="ws-spin" /> Loading scenarios…</div></div>;
  }

  const scenario = workshop.scenarios.find((s) => s.id === selectedId) || workshop.scenarios[0];
  // The model's figures are per charged window; the maps show its busiest hour.
  const periods = periodsOf(workshop);
  const period = periods ? periods.find((p) => p.id === periodId) || periods[0] : null;
  const mapHour = period ? period.mapHour : workshop.default_hour ?? null;
  const shared = { workshop, scenario, onSelect: setSelectedId, period, mapHour, onPeriod: setPeriodId, onServiceLevel };
  const view = OD_VIEWS.some((v) => v.id === odView) ? odView : OD_VIEWS[0].id;
  // The headline cards follow the page shown. The Analytics page has its own scenario and
  // hour: the cards use that scenario and the charged window holding the hour, or the hour
  // alone outside the windows (where every scenario equals the baseline).
  const analyticsShown = page !== 'compare' && view === 'pricing' && !metricOf(anMetric).pricingMap;
  const chargedHours = (periods || []).flatMap((p) => p.hours);
  const hh = (h) => `${String(h % 24).padStart(2, '0')}:00`;
  const cardScenario = analyticsShown
    ? workshop.scenarios.find((s) => s.id === anScenario) || workshop.scenarios[0]
    : scenario;
  const cardPeriod = analyticsShown
    ? (periods || []).find((p) => p.hours.includes(anHour))
      || { id: `hour-${anHour}`, hours: [anHour], mapHour: anHour, label: `${hh(anHour)}–${hh(anHour + 1)}` }
    : period;

  const body = page === 'compare'
    ? (
      <ComparisonPage
        {...shared}
        metrics={metrics}
        onOpenMap={() => { onOdView?.('pricing'); onNavigate?.(menuFor('od')); }}
      />
    )
    : view === 'flows'
      ? <FlowsPage {...shared} />
      : view === 'hexes'
        ? <HexPage {...shared} />
        : metricOf(anMetric).pricingMap
          ? (
            <PricingPage
              {...shared}
              onCompare={() => onNavigate?.(menuFor('compare'))}
              picker={<MetricPicker value={anMetric} onChange={setAnMetric} />}
            />
          )
          : (
            <AnalyticsPage
              cbsa={CBSA}
              workshop={workshop}
              chargedHours={chargedHours}
              metricId={anMetric}
              onMetric={setAnMetric}
              scenarioId={anScenario}
              onScenario={setAnScenario}
              level={anLevel}
              onLevel={setAnLevel}
              hour={anHour}
              onHour={setAnHour}
              header={(
                <Header
                  title="Origin-Destination Analytics"
                  subtitle="Traffic, emissions, air quality and health across the Bay Area by link, hexagon or census tract. Links load for the area in view: zoom in for every road."
                />
              )}
            />
          );

  return (
    <div className="ws-root">
      {page !== 'compare' && onOdView && <OdViewSwitch value={view} onChange={onOdView} />}
      {body}

      <ImpactCards workshop={workshop} scenario={cardScenario} metrics={metrics} period={cardPeriod} />

      <footer className="ws-footer">
        {/* The disclaimer's sentences about "Model" / "illustrative" tags go with the tags. */}
        <FaInfoCircle /> {((workshop.disclaimer || '').match(/[^.]+\.?/g) || []).filter((t) => !/tagged|marked illustrative/i.test(t)).join('').trim()}
      </footer>
    </div>
  );
}

function Header({ title, subtitle, mock, children }) {
  return (
    <header className="ws-header">
      <div>
        <h1>{title}</h1>
        {(subtitle || mock) && (
          <div className="ws-subtitle">
            {subtitle}
            {mock && <span className="ws-tag ws-tag-ill">Mock data</span>}
          </div>
        )}
      </div>
      <div className="ws-header-facts">{children}</div>
    </header>
  );
}

function Fact({ label, children }) {
  return (
    <div className="ws-fact">
      <span className="ws-fact-label">{label}</span>
      <span className="ws-fact-value">{children}</span>
    </div>
  );
}

/** The charged windows the model has figures for; workshop.json's label without a model. */
function TimePeriod({ workshop, period, onPeriod }) {
  const periods = periodsOf(workshop);
  if (!periods) return <Fact label="Time period">{workshop.time_periods[0].label}</Fact>;
  return (
    <div className="ws-fact">
      <span className="ws-fact-label" id="ws-period-label">Time period</span>
      <div className="ws-period" role="group" aria-labelledby="ws-period-label">
        {periods.map((p) => (
          <button key={p.id} type="button" aria-pressed={p.id === period.id}
            className={p.id === period.id ? 'on' : ''} onClick={() => onPeriod(p.id)}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Baseline / Scenario / Difference, or the flow map's Baseline / Scenario.
 *  The baseline has nothing to compare against, so it only draws as itself. */
function ModeToggle({ modes = ROAD_MODES, scenario, mode, onMode }) {
  return (
    <div className="ws-toggle" role="tablist">
      {modes.map((m) => (
        <button
          key={m.id}
          role="tab"
          aria-selected={mode === m.id}
          className={mode === m.id ? 'on' : ''}
          disabled={scenario.id === 'baseline' && m.id !== 'baseline'}
          onClick={() => onMode(m.id)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function useRoadMode(scenario, initial = 'scenario') {
  const [roadMode, setRoadMode] = useState(initial);
  return [scenario.id === 'baseline' ? 'baseline' : roadMode, setRoadMode];
}

const mapNote = (scenario) => (scenario.id === 'baseline' ? null : scenario.map.note);

/** The pricing scenarios on the map: the roads in view, every class once zoomed in,
 *  opening on the charging zone, which is drawn; a way on to Scenario Comparison. The
 *  baseline is not offered here: the Baseline mode under the map shows it. */
function PricingPage({ workshop, scenario, onSelect, onCompare, period, mapHour, onPeriod, onServiceLevel, picker }) {
  const pricing = workshop.scenarios.filter((s) => s.id !== 'baseline');
  const listed = pricing.some((s) => s.id === scenario.id);
  const fallback = (pricing.find((s) => s.id === 'uniform') || pricing[0])?.id;
  useEffect(() => { if (!listed && fallback) onSelect(fallback); }, [listed, fallback, onSelect]);
  const shown = listed ? scenario : pricing.find((s) => s.id === fallback) || scenario;
  const [mode, setMode] = useRoadMode(shown);
  const [mapView, setMapView] = useState(null);
  const roads = useRoads(workshop, shown, mode, mapHour, period, mapView);
  const bands = useBands(workshop, shown.id === routeCostId(workshop));

  return (
    <>
      <Header title="Origin-Destination Analytics" mock={workshop.status === 'mock'}>
        <Fact label="Charging zone">{workshop.zone.label}</Fact>
        <TimePeriod workshop={workshop} period={period} onPeriod={onPeriod} />
      </Header>

      <div className="ws-layout ws-layout-region">
        <WorkshopMap
          zone={workshop.zone}
          focus={workshop.focus}
          roads={roads.data}
          roadMode={mode}
          legendNote={roads.notice ? null : mapNote(shown)}
          onViewChange={setMapView}
          bands={bands}
        >
          <div className="ws-map-top">
            <div className="ws-map-panel">
              <div className="ws-map-panel-title">Traffic on the roads in view</div>
              {picker}
              <label className="ws-muted" htmlFor="ws-map-scenario">Scenario</label>
              <select id="ws-map-scenario" value={shown.id} onChange={(e) => onSelect(e.target.value)}>
                {pricing.map((s) => (
                  <option key={s.id} value={s.id}>{s.code} · {s.name}</option>
                ))}
              </select>
              <ScenarioLevelToggle scenario={shown} onServiceLevel={onServiceLevel} />
              <ModeToggle scenario={shown} mode={mode} onMode={setMode} />
            </div>
          </div>
          <RoadStatus roads={roads} mode={mode} hour={mapHour} period={period} mapView={mapView} />
        </WorkshopMap>

        <div className="ws-col">
          <div className="ws-panel ws-picker">
            <div className="ws-panel-title">Congestion pricing scenarios</div>
            <div className="ws-panel-sub">Pre-computed workshop results only.</div>
            <ScenarioList scenarios={pricing} selectedId={shown.id} onSelect={onSelect} />
            <button className="ws-primary" onClick={onCompare}>Compare scenarios</button>
          </div>
          {shown.id === routeCostId(workshop) && <CorridorPanel workshop={workshop} period={period} />}
        </div>
      </div>
    </>
  );
}

const int = (n) => Math.round(n).toLocaleString('en-US');
const capitalized = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Trips into the zone: car trips from outside the charging zone that end inside it, over
 *  the period's hours, as arrows from origin to destination TAZ and as hexagons by the TAZ
 *  they start in. Baseline draws every one of them; Scenario, by its Show list, those
 *  still made by car, those the charge moved to other modes, or the latter as arrows over
 *  hexagons coloured by the share that shifted. A click on a hexagon or a circle selects
 *  its TAZ: only its arrows stay, and the panel beside the map describes it. */
function FlowsPage({ workshop, scenario, onSelect, period, onPeriod, onServiceLevel }) {
  const [top, setTop] = useRoadMode(scenario, 'baseline');
  // What Scenario shows, kept while Baseline is on.
  const [show, setShow] = useState(SCENARIO_SHOWS[0].id);
  const mode = top === 'baseline' ? 'baseline' : show;
  const [classId, setClassId] = useState(null);
  const [picked, setPicked] = useState(null);
  const [fade, setFade] = useState(45);
  const [showArrows, setShowArrows] = useState(true);
  const [showHexes, setShowHexes] = useState(false);
  const sameTrips = sameTripsAs(workshop, period, scenario);
  const flows = useFlows(workshop, sameTrips || scenario, period);
  const grid = useHexGrid(showHexes || !!picked);
  const bands = useBands(workshop, scenario.id === routeCostId(workshop));
  const doc = flows.data;
  const labels = workshop.model?.classLabels || {};
  const cls = doc && doc.classes && doc.classes.includes(classId) ? classId : null;
  const uncharged = scenario.id === 'baseline';
  const view = useMemo(() => (doc ? flowView(doc, mode, cls) : null), [doc, mode, cls]);
  // A merged circle is the layer's for the trips drawn: other trips merge differently.
  useEffect(() => { setPicked((cur) => (cur && cur.cluster ? null : cur)); }, [view]);
  const locationIds = useMemo(() => (doc ? new Set(doc.locations.map((l) => l.id)) : null), [doc]);
  const selection = useMemo(() => selectionOf(picked, locationIds, grid.data), [picked, locationIds, grid.data]);
  const arrowsOf = selection && selection.locations.length ? selection : null;

  const zone = useMemo(() => (showHexes && grid.data && doc ? getHexZoneTrips(grid.data, doc) : null),
    [showHexes, grid.data, doc]);
  const values = useMemo(() => (zone ? zoneValues(zone, mode, cls) : null), [zone, mode, cls]);
  const bins = useMemo(() => {
    const v = values ? [...values.values()] : [];
    return mode === 'share' ? shareBins(v) : tripBins(v);
  }, [values, mode]);
  const hexes = useMemo(() => (zone
    ? { cells: grid.data.cells, values, colorOf: bins.colorOf, picked: selection ? selection.cells : null }
    : null), [zone, grid.data, values, bins, selection]);

  // Showing the share turns the hexagons on, however it is reached.
  const chooseTop = (id) => {
    setTop(id);
    if (id !== 'baseline' && show === 'share') setShowHexes(true);
  };
  const chooseShow = (id) => {
    setShow(id);
    if (id === 'share') setShowHexes(true);
  };
  const pickHex = (cell) => setPicked((cur) => (cur && cur.hex === cell.id
    ? null
    : { hex: cell.id, taz: String(cell.taz), name: `TAZ ${cell.taz}` }));
  const who = cls ? `${labels[cls]?.name || cls} income: ` : '';
  const describeHex = (cell) => {
    const lines = [`TAZ ${cell.taz}`, `${int(cell.households)} households`];
    if (!zone) return lines;
    const t = zone.byHex.get(cell.id);
    const [n, r] = !t ? [0, 0] : cls ? (t.c && t.c[cls]) || [0, 0] : [t.n, t.r];
    if (!n) return [...lines, `${who}${who ? 'no' : 'No'} car trips into the zone start here`];
    lines.push(`${who}${int(n)} car trips into the zone`);
    if (!uncharged) lines.push(`${int(r)} switched from driving (${pct(r, n)})`);
    lines.push(picked && picked.hex === cell.id ? 'Selected: click again to clear' : 'Click for details');
    return lines;
  };
  const clickable = [showHexes && 'a hexagon', showArrows && 'a circle'].filter(Boolean).join(' or ');

  return (
    <>
      <Header
        title="Origin-Destination Analytics"
        subtitle="Where car trips into the charging zone start, and the ones the charge moves to other modes."
        mock={workshop.status === 'mock'}
      >
        <Fact label="Charging zone">{workshop.zone.label}</Fact>
        <TimePeriod workshop={workshop} period={period} onPeriod={onPeriod} />
      </Header>

      <div className="ws-layout ws-layout-region">
        <FlowMap
          zone={workshop.zone}
          data={showArrows ? view : null}
          selected={arrowsOf}
          onSelect={setPicked}
          fade={fade}
          hexes={hexes}
          describeHex={describeHex}
          onPickHex={pickHex}
          bands={bands}
          legend={(
            <FlowLegend mode={mode} population={doc?.population} showZone={!!workshop.zone?.geometry}
              arrows={showArrows} hexRows={showHexes ? (zone ? bins.rows : []) : null} bands={bands} />
          )}
        >
          <div className="ws-map-top">
            <div className="ws-map-panel ws-flow-panel">
              <div className="ws-map-panel-title">Trips into the charging zone</div>
              <label className="ws-muted" htmlFor="ws-flow-scenario">Scenario</label>
              <select id="ws-flow-scenario" value={scenario.id} onChange={(e) => onSelect(e.target.value)}>
                {workshop.scenarios.map((s) => (
                  <option key={s.id} value={s.id}>{s.code} · {s.name}</option>
                ))}
              </select>
              <ScenarioLevelToggle scenario={scenario} onServiceLevel={onServiceLevel} />
              {sameTrips && (
                <div className="ws-same-note">
                  These are scenario {sameTrips.code}&apos;s trips: {scenario.same_as.why}.
                </div>
              )}
              <ModeToggle modes={FLOW_TOP} scenario={scenario} mode={top} onMode={chooseTop} />
              {top === 'scenario' && (
                <label className="ws-flow-field">
                  <span className="ws-muted">Show</span>
                  <select value={show} onChange={(e) => chooseShow(e.target.value)}>
                    {SCENARIO_SHOWS.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </label>
              )}
              {doc && doc.classes && (
                <label className="ws-flow-field">
                  <span className="ws-muted">Income class</span>
                  <select value={cls || ''} onChange={(e) => setClassId(e.target.value || null)}>
                    <option value="">All drivers</option>
                    {doc.classes.map((k) => (
                      <option key={k} value={k}>
                        {labels[k]?.name || k}{labels[k]?.income ? ` (${labels[k].income})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="ws-flow-show" role="group" aria-label="Show on the map">
                <label>
                  <input type="checkbox" checked={showArrows} onChange={(e) => setShowArrows(e.target.checked)} />
                  Arrows
                </label>
                <label>
                  <input type="checkbox" checked={showHexes} onChange={(e) => setShowHexes(e.target.checked)} />
                  Hexagons
                </label>
              </div>
              {showArrows && (
                <label className="ws-flow-field">
                  <span className="ws-muted">Fade small flows</span>
                  <input type="range" min="0" max="80" step="5" value={fade}
                    onChange={(e) => setFade(Number(e.target.value))} />
                </label>
              )}
            </div>
          </div>
          <div aria-live="polite">
            <FlowStatus flows={flows} doc={doc} cls={cls} selected={arrowsOf} period={period} labels={labels}
              grid={showHexes ? grid : null} />
          </div>
        </FlowMap>

        <div className="ws-col">
          <TazDetails
            selection={selection}
            grid={grid.data}
            doc={doc}
            cls={cls}
            labels={labels}
            period={period}
            hint={clickable
              ? `Click ${clickable} on the map to see its trips into the zone and who lives there.`
              : 'Show arrows or hexagons on the map to pick a TAZ.'}
          />
          <div className="ws-panel ws-picker">
            <div className="ws-panel-title">Congestion pricing scenarios</div>
            <div className="ws-panel-sub">Pre-computed workshop results only.</div>
            <ScenarioList scenarios={workshop.scenarios} selectedId={scenario.id} onSelect={onSelect} />
          </div>
        </div>
      </div>
    </>
  );
}

/** What is picked on the flow map, against the trips' locations: { name, locations whose
 *  arrows stay, leaves the TAZ locations it stands for, cells its hexagon ids }. A
 *  hexagon stands for both parts of a TAZ the cordon cuts, and for no location when no
 *  trip into the zone starts or ends in it; a circle the trips do not have is null. */
function selectionOf(picked, locationIds, grid) {
  if (!picked) return null;
  if (picked.hex) {
    const ids = locationIds ? [picked.taz, `${picked.taz}#in`].filter((id) => locationIds.has(id)) : [];
    return { name: picked.name, locations: ids, leaves: ids, cells: new Set([picked.hex]) };
  }
  if (!locationIds || !(picked.cluster || locationIds.has(picked.id))) return null;
  const cells = new Set();
  for (const id of picked.leaves) {
    const cell = grid && grid.byTaz.get(tazOf(id));
    if (cell) cells.add(cell.id);
  }
  return { name: picked.name, locations: [picked.id], leaves: picked.leaves, cells };
}

/** The flow map's caption: its trips, eligible and shifted, for the period, the class
 *  and the circle chosen; they are the pricing panel's figures for the same hours. */
function FlowStatus({ flows, doc, cls, selected, period, labels, grid }) {
  if (flows.loading) return <div className="ws-map-status"><FaSpinner className="ws-spin" /> Loading trips…</div>;
  if (flows.error) return <div className="ws-map-status ws-error-text">{flows.error}</div>;
  if (flows.notice) return <div className="ws-map-status ws-map-notice">{flows.notice}</div>;
  if (grid && grid.loading) return <div className="ws-map-status"><FaSpinner className="ws-spin" aria-hidden="true" /> Loading hexagons…</div>;
  if (grid && grid.error) return <div className="ws-map-status ws-error-text">{grid.error}</div>;
  if (!doc) return null;
  const t = flowTotals(doc, cls, selected ? selected.leaves : null);
  const who = [cls && (labels[cls]?.name ? `${labels[cls].name} income` : cls), selected && selected.name]
    .filter(Boolean).join(' · ');
  const all = !cls && !selected;
  const through = all ? doc.totals.through_paying : undefined;
  const throughNoCharge = all && through === undefined ? doc.totals.through_paying_baseline : undefined;
  return (
    <div className="ws-map-status ws-flow-status">
      <span>
        {who && <b>{who}: </b>}
        {int(t.eligible)} eligible car trips into the zone
        {t.shifted !== null && (
          <>, {int(t.shifted)} shifted to other modes ({t.eligible ? ((100 * t.shifted) / t.eligible).toFixed(1) : '0.0'}%)</>
        )}
        {all && doc.totals.extra_removed !== undefined && (
          <>, {int(doc.totals.extra_removed)} of them because of the extra departures</>
        )}
        {' · '}<span className="ws-nowrap">{period.label}</span>
        {through !== undefined && <>{' · '}{int(through)} through trips not drawn</>}
        {throughNoCharge !== undefined && <>{' · '}{int(throughNoCharge)} through trips not drawn (the no-charge count)</>}
        {selected && <span className="ws-flow-hint">Click it again, or anywhere empty, to show them all.</span>}
        {doc.mismatches.length > 0 && (
          <span className="ws-flow-warn" title={doc.mismatches.join('\n')}>
            These trips do not add up to the pricing panel&apos;s figures for these hours.
          </span>
        )}
      </span>
    </div>
  );
}

/** `hexRows`: the hexagons' colour rows while they are shown, null otherwise. */
function FlowLegend({ mode, population, showZone, arrows, hexRows, bands }) {
  const m = FLOW_MODES.find((x) => x.id === mode) || FLOW_MODES[0];
  return (
    <div className="ws-legend">
      {arrows && (
        <div className="ws-legend-part">
          <div className="ws-legend-title">{m.legend}</div>
          <div className="ws-legend-row"><span className="ws-swatch ws-swatch-flow" />Origin → destination TAZ; brighter, more trips</div>
          <div className="ws-legend-row"><span className="ws-swatch-dot" />A TAZ, or nearby TAZs merged until you zoom in</div>
        </div>
      )}
      {hexRows && (
        <div className="ws-legend-part">
          <div className="ws-legend-title">{m.hexLegend}</div>
          <HexLegendRows rows={hexRows} empty="No trips to colour." />
          {SELECTED_ROW}
          {mode === 'share' && (
            <div className="ws-legend-note ws-legend-sub">
              Hexagons with fewer than {SHARE_MIN_TRIPS} trips into the zone are not coloured.
            </div>
          )}
        </div>
      )}
      {showZone && (
        <div className="ws-legend-row"><span className="ws-swatch ws-swatch-zone" />Congestion pricing zone</div>
      )}
      <BandsLegendRow bands={bands} />
      {population && <div className="ws-legend-note ws-legend-sub">{capitalized(population)}.</div>}
    </div>
  );
}

/** The hexagon views. 'car': a click on a hexagon colours the others by its car trips to
 *  each, the baseline's: a charge removes only trips into the zone that switch from
 *  driving. 'travel': a click colours the others by travel time from it, asked of
 *  Valhalla on the click; neither scenario nor period changes it. */
const HEX_VIEWS = [
  { id: 'car', label: 'Car trips (all destinations)', hint: 'Click a hexagon to colour the map by where its car trips go.' },
  { id: 'travel', label: 'Travel time', hint: 'Click a hexagon to colour the map by how long it takes to get from it to each.' },
];

const HEX_TOP = 10;
const REACH_BY = { walk: 'on foot', bike: 'by bike', drive: 'by car', transit: 'by transit' };
const CHEAP_MODES = REACH_MODES.filter((m) => m.id !== 'transit');
const pct = (part, whole) => (whole ? `${((100 * part) / whole).toFixed(1)}%` : '—');

/** Accessibility: the Bay Area's hexagons (the model's TAZs), over the period's hours.
 *  Car trips: a click on one colours the others by its car trips to each (/od-all). */
function HexPage({ workshop, scenario, onSelect, period, mapHour, onPeriod }) {
  const grid = useHexGrid();
  const [originId, setOriginId] = useState(null);
  const [viewId, setViewId] = useState('car');
  const [travelMode, setTravelMode] = useState('drive');
  const view = HEX_VIEWS.find((v) => v.id === viewId);
  const travelView = view.id === 'travel';
  const origin = originId && grid.data ? grid.data.byId.get(originId) : null;

  const carTrips = useOdAll(workshop, view.id === 'car' && origin ? String(origin.taz) : null, period);
  // Transit leaves at half past the period's map hour, as on the other maps.
  const transitHour = mapHour ?? TRANSIT_DEFAULT_HOUR;
  const leaving = `${String(transitHour).padStart(2, '0')}:30`;
  const reach = useReach(grid.data, travelView ? origin : null, transitHour, travelMode === 'transit');
  const modeBy = REACH_BY[travelMode];
  const time = travelView && origin ? reach.byMode[travelMode] : null;

  const trips = useMemo(() => (view.id === 'car' && grid.data && originId && carTrips.data
    ? getHexCarTrips(grid.data, originId, carTrips.data)
    : null), [view.id, grid.data, originId, carTrips.data]);
  const minutesTo = time && time.data ? time.data : null;
  const values = useMemo(() => (trips ? new Map(trips.trips) : null), [trips]);
  const bins = useMemo(() => tripBins(values ? [...values.values()] : []), [values]);
  // The Accessibility map draws no protected neighbourhoods (2026-10-28): they belong to the
  // traffic views, not to travel times and trips from a hexagon.
  const bands = useBands(workshop, false);

  const describe = (cell) => {
    const lines = [`TAZ ${cell.taz}`, `${int(cell.households)} households`];
    if (travelView) {
      if (cell.id === originId) lines.push('Selected: travel times start here');
      else if (!origin) lines.push('Click to make it the origin');
      else if (minutesTo) lines.push(reachPhrase(minutesTo.get(cell.id), travelMode));
      return lines;
    }
    if (cell.id === originId) lines.push('Selected: trips start here');
    else if (values) lines.push(`${int(values.get(cell.id) || 0)} car trips from the selected hexagon`);
    else lines.push('Click to see where its car trips go');
    return lines;
  };

  return (
    <>
      <Header
        title="Origin-Destination Analytics"
        subtitle={travelView
          ? 'How far people get from each hexagon, by mode. Click a hexagon to make it the origin.'
          : (
            <>
              Car trips from the selected hexagon to every other, by destination. Baseline
              counts: the scenarios remove only trips into the zone that switch from driving. <ModelTag />
            </>
          )}
      >
        <Fact label="Hexagons">H3 resolution 7 (the model&apos;s TAZs)</Fact>
        <TimePeriod workshop={workshop} period={period} onPeriod={onPeriod} />
        {travelView && <Fact label="Travel times">Valhalla · transit leaving {leaving}</Fact>}
      </Header>

      <div className="ws-layout ws-layout-region">
        <HexMap
          cells={grid.data ? grid.data.cells : []}
          values={travelView ? minutesTo : values}
          colorOf={travelView ? reachColor : bins.colorOf}
          selectedId={originId}
          onSelect={setOriginId}
          describe={describe}
          bands={bands}
          legend={travelView
            ? <ReachLegend mode={travelMode} leaving={leaving} bands={bands} />
            : <CarLegend rows={trips ? bins.rows : []} bands={bands} />}
        >
          <div className="ws-map-top">
            <div className="ws-map-panel">
              <div className="ws-map-panel-title">Accessibility</div>
              <div className="ws-toggle" role="group" aria-label="Trips drawn">
                {HEX_VIEWS.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    aria-pressed={view.id === v.id}
                    className={view.id === v.id ? 'on' : ''}
                    onClick={() => setViewId(v.id)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <div className="ws-same-note">{view.hint}</div>
              {travelView && (
                <div className="ws-toggle" role="group" aria-label="Travel mode">
                  {REACH_MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      aria-pressed={travelMode === m.id}
                      className={travelMode === m.id ? 'on' : ''}
                      onClick={() => setTravelMode(m.id)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
              {origin && (
                <button type="button" className="ws-hex-clear" onClick={() => setOriginId(null)}>Clear selection</button>
              )}
            </div>
          </div>
          <div aria-live="polite">
            {travelView
              ? <TravelStatus grid={grid} origin={origin} time={time} modeBy={modeBy} onRetry={reach.retry} />
              : <CarStatus grid={grid} source={carTrips} origin={origin} trips={trips} period={period} />}
          </div>
        </HexMap>

        <div className="ws-col">
          {travelView
            ? (
              <TravelDetails origin={origin} grid={grid.data} reach={reach.byMode} mode={travelMode} onMode={setTravelMode}
                onLoadTransit={reach.loadTransit} onRetry={reach.retry} />
            )
            : <CarDetails origin={origin} trips={trips} grid={grid.data} colorOf={bins.colorOf} period={period} />}
          {!travelView && (
            <div className="ws-panel ws-picker">
              <div className="ws-panel-title">Congestion pricing scenarios</div>
              <div className="ws-panel-sub">Pre-computed workshop results only.</div>
              <ScenarioList scenarios={workshop.scenarios} selectedId={scenario.id} onSelect={onSelect} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function HexLegendRows({ rows, empty }) {
  return rows.length ? rows.map((r) => (
    <div className="ws-legend-row" key={r.label}>
      <span className="ws-swatch-hex" style={{ background: `rgb(${r.color.join(',')})` }} />{r.label}
    </div>
  )) : <div className="ws-legend-row ws-muted">{empty}</div>;
}

const SELECTED_ROW = <div className="ws-legend-row"><span className="ws-swatch-hex ws-swatch-hex-picked" />Selected hexagon</div>;

function CarStatus({ grid, source, origin, trips, period }) {
  if (grid.loading) return <div className="ws-map-status"><FaSpinner className="ws-spin" /> Loading hexagons…</div>;
  if (grid.error) return <div className="ws-map-status ws-error-text">{grid.error}</div>;
  if (source.loading) return <div className="ws-map-status"><FaSpinner className="ws-spin" /> Loading trips…</div>;
  if (source.error) return <div className="ws-map-status ws-error-text">{source.error}</div>;
  if (source.notice) return <div className="ws-map-status ws-map-notice">{source.notice}</div>;
  if (!origin || !trips) {
    return <div className="ws-map-status">{int(grid.data.cells.length)} hexagons · click one to see where its car trips go</div>;
  }
  if (!trips.total) return <div className="ws-map-status">No car trips start in TAZ {origin.taz} in these hours.</div>;
  return (
    <div className="ws-map-status">
      {int(trips.total)} car trips from TAZ {origin.taz} to {int(trips.trips.length)} hexagons
      {' · '}<span className="ws-nowrap">{period.label}</span>
    </div>
  );
}

function CarLegend({ rows, bands }) {
  return (
    <div className="ws-legend">
      <div className="ws-legend-title">Car trips from the selected hexagon</div>
      <HexLegendRows rows={rows} empty="Click a hexagon to colour the map." />
      {SELECTED_ROW}
      <BandsLegendRow bands={bands} />
    </div>
  );
}

/** The selected hexagon: who lives there, and where its car trips go. */
function CarDetails({ origin, trips, grid, colorOf, period }) {
  if (!origin) {
    return (
      <div className="ws-panel">
        <div className="ws-panel-title">Selected hexagon</div>
        <div className="ws-panel-sub">
          Click a hexagon on the map. The others are then coloured by the trips from it to each.
        </div>
      </div>
    );
  }
  const none = trips && !trips.total ? `No car trips start in TAZ ${origin.taz} in these hours.` : null;
  const top = trips && !none ? trips.trips.slice(0, HEX_TOP) : [];
  return (
    <div className="ws-panel">
      <div className="ws-panel-title">TAZ {origin.taz}</div>
      <HexResidents origin={origin} />
      {none && <div className="ws-panel-sub">{none}</div>}
      {trips && !none && (
        <>
          <div className="ws-panel-sub">
            {int(trips.total)} car trips start here{' · '}{period.label} <ModelTag />
          </div>
          <table className="ws-table ws-hex-table">
            <thead>
              <tr>
                <th>Top destinations</th>
                <th>Car trips</th>
                <th>Share</th>
                <th>Distance</th>
              </tr>
            </thead>
            <tbody>
              {top.map(([id, n]) => {
                const dest = grid.byId.get(id);
                return (
                  <tr key={id}>
                    <td>
                      <span className="ws-swatch-hex" style={{ background: `rgb(${colorOf(n).join(',')})` }} />
                      TAZ {dest?.taz ?? id}
                    </td>
                    <td>{int(n)}</td>
                    <td>{pct(n, trips.total)}</td>
                    <td>{dest ? `${hexDistanceKm(origin, dest).toFixed(1)} km` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function HexResidents({ origin }) {
  return (
    <div className="ws-hex-facts">
      <div><span className="ws-muted">Households</span>{int(origin.households)}</div>
      <div><span className="ws-muted">Residents</span>{int(origin.population)}</div>
      <div>
        <span className="ws-muted">Low-income</span>
        {origin.lowIncomeShare == null ? '—' : `${Math.round(origin.lowIncomeShare * 100)}%`}
      </div>
      <div>
        <span className="ws-muted">Median income</span>
        {origin.medianIncome ? `$${int(origin.medianIncome)}` : '—'}
      </div>
    </div>
  );
}

/** The TAZ, or merged circle, picked on the flow map: who lives there, and its trips into
 *  the zone, all drivers' or the class chosen, then by income class where they have one. */
function TazDetails({ selection, grid, doc, cls, labels, period, hint }) {
  if (!selection) {
    return (
      <div className="ws-panel">
        <div className="ws-panel-title">Selected TAZ</div>
        <div className="ws-panel-sub">{hint}</div>
      </div>
    );
  }
  const cells = grid ? [...selection.cells].map((id) => grid.byId.get(id)).filter(Boolean) : [];
  const sum = (key) => cells.reduce((s, c) => s + (c[key] || 0), 0);
  const residents = cells.length > 1
    ? { households: sum('households'), population: sum('population'), lowIncomeShare: null, medianIncome: null }
    : cells[0];
  const t = doc && selection.leaves.length ? flowTotals(doc, cls, selection.leaves) : null;
  const uncharged = !!t && t.shifted === null;
  return (
    <div className="ws-panel">
      <div className="ws-panel-title">{selection.name}</div>
      {residents && <HexResidents origin={residents} />}
      {doc && !t && (
        <div className="ws-panel-sub">No car trips from {selection.name} end in the charging zone in these hours.</div>
      )}
      {t && (
        <>
          <div className="ws-panel-sub">
            Trips into the charging zone{cls && <>{' · '}{labels[cls]?.name || cls} income</>}{' · '}{period.label} <ModelTag />
          </div>
          <div className="ws-hex-facts">
            <div><span className="ws-muted">Car trips into the zone</span>{int(t.eligible)}</div>
            <div><span className="ws-muted">Shifted (trips)</span>{uncharged ? '— (no charge)' : int(t.shifted)}</div>
            <div><span className="ws-muted">Shifted (% of trips)</span>{uncharged ? '—' : pct(t.shifted, t.eligible)}</div>
          </div>
          {selection.leaves.some((id) => id.endsWith('#in')) && (
            <div className="ws-panel-sub">Counts the trips that end in its part inside the zone as well as those that start here; the hexagon’s colour counts only the latter.</div>
          )}
          {t.byClass && (
            <table className="ws-table ws-hex-table">
              <thead>
                <tr>
                  <th>Income class</th>
                  <th>Car trips</th>
                  <th>Shifted</th>
                  <th>% shifted</th>
                </tr>
              </thead>
              <tbody>
                {doc.classes.map((k) => {
                  const [n, r] = t.byClass[k];
                  return (
                    <tr key={k} className={k === cls ? 'selected' : undefined}>
                      <td>
                        {labels[k]?.name || k}
                        {labels[k]?.income && <span className="ws-cell-detail">{labels[k].income}</span>}
                      </td>
                      <td>{int(n)}</td>
                      <td>{int(r)}</td>
                      <td>{pct(r, n)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!t.byClass && !uncharged && (
            <div className="ws-panel-sub">This scenario&apos;s trips are not split by income class.</div>
          )}
        </>
      )}
    </div>
  );
}

/** Walk, bike and drive minutes are a band's upper edge (the hexagon's centre inside
 *  that isochrone); transit's the earliest arrival anywhere in the hexagon. */
function reachPhrase(minutes, mode) {
  const by = REACH_BY[mode];
  if (minutes === undefined) return `Over an hour ${by}`;
  if (mode === 'transit') return `${Math.round(minutes)} min ${by}`;
  const i = REACH_BANDS.findIndex((band) => band.upTo === minutes);
  return i > 0 ? `${REACH_BANDS[i - 1].upTo}–${minutes} min ${by}` : `Up to ${minutes} min ${by}`;
}

function TravelStatus({ grid, origin, time, modeBy, onRetry }) {
  if (grid.loading) return <div className="ws-map-status"><FaSpinner className="ws-spin" /> Loading hexagons…</div>;
  if (grid.error) return <div className="ws-map-status ws-error-text">{grid.error}</div>;
  if (!origin) return <div className="ws-map-status">{int(grid.data.cells.length)} hexagons · click one to make it the origin</div>;
  if (!time || time.loading || time.idle) {
    return <div className="ws-map-status"><FaSpinner className="ws-spin" /> Working out travel times {modeBy}…</div>;
  }
  if (time.busy) {
    return (
      <div className="ws-map-status ws-map-notice">
        <span>Transit times are busy, <button type="button" className="ws-inline-btn" onClick={onRetry}>try again</button></span>
      </div>
    );
  }
  if (time.error) return <div className="ws-map-status ws-error-text">{time.error}</div>;
  return (
    <div className="ws-map-status">
      {int(time.data.size)} hexagons within an hour {modeBy} from TAZ {origin.taz}
    </div>
  );
}

function ReachLegend({ mode, leaving, bands }) {
  return (
    <div className="ws-legend">
      <div className="ws-legend-title">Travel time from the selected hexagon</div>
      {REACH_BANDS.map((b, i) => (
        <div className="ws-legend-row" key={b.upTo}>
          <span className="ws-swatch-hex" style={{ background: `rgb(${b.color.join(',')})` }} />
          {i === 0 ? `Up to ${b.upTo} min` : `${REACH_BANDS[i - 1].upTo}–${b.upTo} min`}
        </div>
      ))}
      {SELECTED_ROW}
      <BandsLegendRow bands={bands} />
      <div className="ws-legend-note ws-legend-sub">
        {mode === 'transit' && `Walking and transit, leaving at ${leaving} on a weekday (511 schedules). `}
        {mode === 'drive' && 'Free-flow driving: no traffic or parking time. '}
        {mode !== 'transit' && 'By where each hexagon’s centre falls. '}
        Starting from the public street nearest the selected hexagon’s centre. Uncoloured: over an hour.
      </div>
    </div>
  );
}

/** The four modes side by side: how many hexagons each reaches within 30 and 60 minutes. */
function TravelDetails({ origin, grid, reach, mode, onMode, onLoadTransit, onRetry }) {
  if (!origin) {
    return (
      <div className="ws-panel">
        <div className="ws-panel-title">Selected hexagon</div>
        <div className="ws-panel-sub">
          Click a hexagon on the map. Every other hexagon is then coloured by how long it takes
          to get there, and the four modes are compared here.
        </div>
      </div>
    );
  }
  const cell = (r, minutes) => {
    if (!r || r.loading) return <FaSpinner className="ws-spin" role="img" aria-label="Loading" />;
    if (r.error) return <span className="ws-error-text" title={r.error} aria-label={r.error}>—</span>;
    return int(reachHexes(r.data, minutes));
  };
  // The row's own click puts the mode on the map; these only fetch the numbers.
  const action = (label, aria, onClick) => (
    <td colSpan={2}>
      <button type="button" className="ws-load-btn" aria-label={aria}
        onClick={(e) => { e.stopPropagation(); onClick(); }}>
        {label}
      </button>
    </td>
  );
  return (
    <div className="ws-panel">
      <div className="ws-panel-title">TAZ {origin.taz}</div>
      <HexResidents origin={origin} />
      <table className="ws-table ws-reach-table">
        <thead><tr><th>Hexagons reachable</th><th>≤ 30 min</th><th>≤ 60 min</th></tr></thead>
        <tbody>
          {REACH_MODES.map((m) => (
            <tr key={m.id} className={m.id === mode ? 'selected' : ''} onClick={() => onMode(m.id)}>
              <td>
                <button type="button" className="ws-cell-btn" aria-pressed={m.id === mode}>{m.label}</button>
              </td>
              {reach[m.id]?.idle && action('Load', `Load ${m.label.toLowerCase()} travel times`, onLoadTransit)}
              {reach[m.id]?.busy && action('Busy, try again', `${m.label} travel times are busy, try again`, onRetry)}
              {!reach[m.id]?.idle && !reach[m.id]?.busy && (
                <>
                  <td>{cell(reach[m.id], 30)}</td>
                  <td>{cell(reach[m.id], 60)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="ws-muted">
        Of {int(grid.cells.length)} hexagons in the region.
      </div>
    </div>
  );
}

const failed = (r) => !!r && (r.busy || !!r.error);

/** Settles one reach request into `byKey`, showing it as loading again if it had failed. */
function track(setByKey, key, promise) {
  setByKey((cur) => (failed(cur[key]) ? { ...cur, [key]: { loading: true } } : cur));
  promise
    .then((data) => setByKey((cur) => (cur[key]?.data === data ? cur : { ...cur, [key]: { data } })))
    .catch((e) => setByKey((cur) => ({
      ...cur,
      [key]: e.busy ? { busy: true } : { error: `Could not get travel times: ${e.message}` },
    })));
}

/** Minutes to every hexagon from `origin` by each mode, for the map and the panel that
 *  compares them: byMode { walk: { loading } | { data } | { error } | { busy } | { idle },
 *  ... }, empty while no origin. Walk, bike and drive are asked for on the click; transit,
 *  one large search on the backend, only while it is on the map (`transitOnMap`) or after
 *  loadTransit(), leaving at `hour`:30. retry() asks again for whatever failed. */
function useReach(grid, origin, hour, transitOnMap) {
  const [byKey, setByKey] = useState({});
  const [askedKey, setAskedKey] = useState(null);
  const transitKey = origin ? reachKey(origin.id, 'transit', hour) : null;
  const transitWanted = !!transitKey && (transitOnMap || askedKey === transitKey);

  useEffect(() => {
    if (!grid || !origin) return;
    CHEAP_MODES.forEach(({ id }) => track(setByKey, reachKey(origin.id, id), getReach(grid, origin, id)));
  }, [grid, origin]);

  useEffect(() => {
    if (!grid || !origin || !transitWanted) return;
    track(setByKey, reachKey(origin.id, 'transit', hour), getReach(grid, origin, 'transit', hour));
  }, [grid, origin, hour, transitWanted]);

  const retry = () => {
    if (!grid || !origin) return;
    REACH_MODES.forEach(({ id }) => {
      const key = reachKey(origin.id, id, hour);
      if (failed(byKey[key])) track(setByKey, key, getReach(grid, origin, id, hour));
    });
  };
  const loadTransit = () => setAskedKey(transitKey);

  const byMode = origin
    ? Object.fromEntries(REACH_MODES.map(({ id }) => {
      const r = byKey[reachKey(origin.id, id, hour)];
      return [id, r || (id === 'transit' && !transitWanted ? { idle: true } : { loading: true })];
    }))
    : {};
  return { byMode, loadTransit, retry };
}

const NO_BANDS = {};

/** The protected neighbourhoods while `on` (the route-cost scenario is the one chosen):
 *  { outlines } once read (cached in api.js, the same at every hour), { error } if they
 *  could not be; {} meanwhile and while off. */
function useBands(workshop, on) {
  const [state, setState] = useState(NO_BANDS);
  useEffect(() => {
    if (!on) return undefined;
    let cancelled = false;
    getBandOutlines(workshop)
      .then((outlines) => { if (!cancelled) setState((cur) => (cur.outlines === outlines ? cur : { outlines })); })
      .catch((e) => {
        if (!cancelled) {
          setState({ error: `Protected neighbourhoods could not be loaded (${e.message}). Pick another scenario, then this one again, to retry.` });
        }
      });
    return () => { cancelled = true; };
  }, [workshop, on]);
  return on ? state : NO_BANDS;
}

/** The hexagon grid, fetched once `wanted`. */
function useHexGrid(wanted = true) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    if (!wanted) return undefined;
    let cancelled = false;
    getHexGrid()
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, data: null, error: `Could not load hexagons: ${e.message}` }); });
    return () => { cancelled = true; };
  }, [wanted]);
  return state;
}

/** Every car trip from the TAZ `taz` over the period's hours (cached in api.js); idle
 *  while no TAZ is picked. */
function useOdAll(workshop, taz, period) {
  const hours = period ? period.hours : null;
  const key = workshop && taz && hours ? `${taz}:${hours.join(',')}` : null;
  const [state, setState] = useState({ key: null, loading: false, data: null, error: null });

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setState({ key, loading: true, data: null, error: null });
    getOdAll(workshop, taz, hours)
      .then((data) => { if (!cancelled) setState({ key, loading: false, data, error: null }); })
      .catch((e) => { if (!cancelled) setState({ key, loading: false, data: null, error: `Could not load trips: ${e.message}` }); });
    return () => { cancelled = true; };
  }, [key, workshop, taz, hours]);

  if (taz && !hours) return { loading: false, data: null, error: null, notice: 'This metro has no charged hours to show.' };
  return state.key === key ? state : { loading: !!key, data: null, error: null };
}

/** Scenario Comparison: the metric grid, with a map per scenario at the head of its column. */
function ComparisonPage({ workshop, metrics, scenario, onSelect, onOpenMap, period, mapHour, onPeriod, onServiceLevel }) {
  return (
    <>
      <Header
        title="Scenario Comparison"
        subtitle="Compare the pre-computed scenarios side by side."
        mock={workshop.status === 'mock'}
      >
        <Fact label="Location">San Francisco, CA</Fact>
        <TimePeriod workshop={workshop} period={period} onPeriod={onPeriod} />
      </Header>

      <ComparisonGrid workshop={workshop} metrics={metrics} period={period} selectedId={scenario.id} onSelect={onSelect}
        onServiceLevel={onServiceLevel}
        thumb={(s) => (
          <ScenarioThumb
            workshop={workshop}
            scenario={s}
            selected={s.id === scenario.id}
            onSelect={() => onSelect(s.id)}
            onOpen={SHOW_OD_PRICING_TABS ? () => { onSelect(s.id); onOpenMap(); } : undefined}
            mapHour={mapHour}
            period={period}
          />
        )} />
    </>
  );
}

function ScenarioThumb({ workshop, scenario, selected, onSelect, onOpen, mapHour, period }) {
  const mode = scenario.id === 'baseline' ? 'baseline' : 'scenario';
  const roads = useRoads(workshop, scenario, mode, mapHour, period);
  return (
    <div
      className={`ws-thumb ${selected ? 'selected' : ''}`}
      style={selected ? { borderColor: scenario.color } : null}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onDoubleClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      title={onOpen ? 'Click to select · double-click to open on the map' : 'Click to select'}
    >
      <div className="ws-thumb-head">
        <span className="ws-code" style={{ background: scenario.color }}>{scenario.code}</span>
        <span className="ws-thumb-name">{scenario.name}</span>
        {scenario.level && <span className="ws-thumb-level">{scenario.level.label}</span>}
      </div>
      <ThumbMap zone={workshop.zone} focus={workshop.focus} roads={roads.data} />
      {roads.loading && <div className="ws-thumb-status"><FaSpinner className="ws-spin" /></div>}
      {roads.error && <div className="ws-thumb-error" role="status">{roads.error}</div>}
      {roads.notice && <div className="ws-thumb-notice" role="status">{roads.notice}</div>}
    </div>
  );
}

function RoadStatus({ roads, mode, hour = null, period = null, mapView = undefined }) {
  if (roads.loading) {
    return <div className="ws-map-status"><FaSpinner className="ws-spin" /> Loading roads…</div>;
  }
  if (roads.error) return <div className="ws-map-status ws-error-text">{roads.error}</div>;
  if (roads.notice) return <div className="ws-map-status ws-map-notice">{roads.notice}</div>;
  if (!roads.data) return null;
  const n = roads.data.features.length.toLocaleString();
  const where = mapView !== undefined ? ' in the whole region' : '';
  let text;
  if (mode === 'difference') {
    text = roads.data.truncated
      ? `The ${n} road segments${where} whose V/C changed most (all by 0.02 or more)`
      : `${n} road segments${where} whose V/C changed by 0.02 or more`;
  } else if (mapView) {
    text = `${n} road segments in view`
      + (roadTier(mapView.zoom).what ? ` (${roadTier(mapView.zoom).what}; zoom in for every road)` : '')
      + (roads.data.truncated ? ', not all of them: zoom in' : '');
  } else {
    text = `${n} road segments around the zone`;
  }
  return (
    <div className="ws-map-status">
      <span>
        {text}
        {hour !== null && (
          <> · <span className="ws-nowrap">{hourSpan(hour)}</span>{period && <>, the busiest hour of <span className="ws-nowrap">{period.label}</span></>}</>
        )}
      </span>
    </div>
  );
}

/** Trips into the zone for a scenario's run over the period's hours (cached in api.js).
 *  A run the model has no figures for in the period has none here either: the reason
 *  comes back as `notice`, as for the roads. */
function useFlows(workshop, scenario, period) {
  const run = scenario?.map.run_scenario;
  const notice = runNotice(workshop, run, period);
  const hours = period ? period.hours : null;
  const key = workshop && run && hours && !notice ? `${run}:${hours.join(',')}` : null;
  const [state, setState] = useState({ key: null, loading: false, data: null, error: null });

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setState({ key, loading: true, data: null, error: null });
    Promise.resolve()
      .then(() => getFlows(workshop, run, hours))
      .then((data) => { if (!cancelled) setState({ key, loading: false, data, error: null }); })
      .catch((e) => { if (!cancelled) setState({ key, loading: false, data: null, error: `Could not load trips: ${e.message}` }); });
    return () => { cancelled = true; };
  }, [key, workshop, run, hours]);

  if (notice) return { loading: false, data: null, error: null, notice };
  if (!hours) return { loading: false, data: null, error: null, notice: 'This metro has no charged hours to show.' };
  return state.key === key ? state : { loading: !!key, data: null, error: null };
}

/** Roads for one scenario in one map mode at one hour, from the stored runs (cached in
 *  api.js). A run the model has no figures for in the period has no map either: the
 *  reason comes back as `notice`. */
function useRoads(workshop, scenario, mode, hour = null, period = null, mapView = undefined) {
  const run = mode === 'baseline' ? 'baseline' : scenario?.map.run_scenario;
  const notice = runNotice(workshop, run, period);
  const kind = mode === 'difference' ? 'difference' : 'run';
  // mapView undefined: the box around the zone. null: the roads in view, the map has not
  // said yet. The difference stays region-wide: /compare takes no area.
  const inView = kind === 'run' && mapView !== undefined;
  const base = `${kind}:${run}:${hour}`;
  const key = workshop && run && !notice && (!inView || mapView)
    ? (inView
      ? `${base}:${mapView.bounds.map((x) => x.toFixed(4)).join(',')}:${roadTier(mapView.zoom).id}`
      : base)
    : null;
  const [state, setState] = useState({ key: null, base: null, loading: false, data: null, error: null });
  const viewNow = useRef(mapView);
  viewNow.current = mapView;

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setState((prev) => ({ key, base, loading: true, data: prev.base === base ? prev.data : null, error: null }));
    // A throw inside getRoads must end up in .catch, not escape the effect.
    Promise.resolve()
      .then(() => (inView ? getViewRoads(workshop, run, hour, viewNow.current) : getRoads(workshop, run, kind, hour)))
      .then((data) => { if (!cancelled) setState({ key, base, loading: false, data, error: null }); })
      .catch((e) => { if (!cancelled) setState({ key, base, loading: false, data: null, error: `Could not load roads: ${e.message}` }); });
    return () => { cancelled = true; };
  }, [key, base, workshop, run, kind, hour, inView]);

  if (notice) return { loading: false, data: null, error: null, notice };
  if (inView && !mapView) return { loading: true, data: null, error: null };
  if (state.key === key) return state;
  // Never hand back another mode's roads: they would be coloured by the wrong field. The
  // same roads in another view stay up while it loads.
  return { loading: !!key, data: state.base === base ? state.data : null, error: null };
}
